/**
 * Main Dashboard Layout
 * Responsive sidebar navigation and main content area
 * Mobile: Hamburger menu → Slide-in drawer
 * Desktop: Persistent sidebar
 */

import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Package,
  MessageSquare,
  BarChart3,
  Settings,
  Package2,
  Search,
  Users,
  FileText,
  LogOut,
  Moon,
  Sun,
  Menu,
  X,
  AlertTriangle,
} from 'lucide-react';
import logoUrl from '../assets/logo.svg';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { fetchAdminUnreadCount } from '../api/chat';
import { fetchMaintenanceSettings } from '../api/admin';
import { useChatWebSocket } from '../hooks/useChatWebSocket';
import { useIsMobileOrTablet } from '../hooks/useMediaQuery';

const sidebarItems = [
  { id: 'orders', labelKey: 'sidebar.orders', icon: Package, path: '/orders', permissions: ['read', 'staff'] },
  { id: 'chat', labelKey: 'sidebar.chat', icon: MessageSquare, path: '/chat', permissions: ['read', 'staff'] },
  { id: 'monitoring', labelKey: 'sidebar.monitoring', icon: BarChart3, path: '/monitoring', permissions: ['read', 'staff'] },
  { id: 'products', labelKey: 'sidebar.products', icon: Package2, path: '/products', permissions: ['admin'] },
  { id: 'stickers', labelKey: 'sidebar.stickers', icon: Package, path: '/stickers', permissions: ['staff', 'admin'] },
  { id: 'settings', labelKey: 'sidebar.settings', icon: Settings, path: '/settings', permissions: ['admin'] },
  { id: 'query', labelKey: 'sidebar.query', icon: Search, path: '/query', permissions: ['admin'] },
  { id: 'users', labelKey: 'sidebar.users', icon: Users, path: '/users', permissions: ['admin'] },
  { id: 'logs', labelKey: 'sidebar.logs', icon: FileText, path: '/logs', permissions: ['admin'] },
];

export function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(() =>
    new Date(),
  );

  // Detect mobile/tablet for responsive behavior
  const isMobileOrTablet = useIsMobileOrTablet();

  // WebSocket for real-time unread count updates
  const { isConnected } = useChatWebSocket();

  // Fetch unread count - WebSocket provides real-time updates, HTTP polling is fallback
  // Polling disabled when WebSocket connected, fallback to 60s when disconnected
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['chat-unread-count'],
    queryFn: fetchAdminUnreadCount,
    refetchInterval: isConnected ? false : 60000, // Disable polling if WebSocket connected
  });

  // Fetch maintenance settings to show warning banner
  const { data: maintenanceSettings } = useQuery({
    queryKey: ['maintenanceSettings'],
    queryFn: fetchMaintenanceSettings,
    staleTime: 30000, // 30 seconds
  });

  const isMaintenanceMode = maintenanceSettings?.enabled ?? false;

  // Filter sidebar items based on user permissions
  const visibleSidebarItems = sidebarItems.filter(item => {
    // Check if user has any of the required permissions for this item
    return item.permissions.some(permission => hasPermission(permission));
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentPath = location.pathname;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  // Close mobile sidebar when navigating
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [currentPath]);

  // Close mobile sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileSidebarOpen) {
        setMobileSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileSidebarOpen]);

  return (
    <div className={`flex h-screen bg-stone-100 ${theme === 'dark' ? 'dark' : ''}`}>
      {/* Mobile Backdrop Overlay */}
      {isMobileOrTablet && mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          ${isMobileOrTablet ? 'fixed inset-y-0 left-0 z-50' : 'relative'}
          ${isMobileOrTablet && !mobileSidebarOpen ? '-translate-x-full' : 'translate-x-0'}
          ${sidebarCollapsed && !isMobileOrTablet ? 'w-16' : 'w-64'}
          flex h-full flex-col bg-white dark:bg-gray-800 shadow-lg
          transition-all duration-300 ease-spring
          lg:translate-x-0
        `}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-200 dark:border-gray-700">
          {(!sidebarCollapsed || isMobileOrTablet) && (
            <div className="flex items-center gap-2">
              <img
                src={logoUrl}
                alt="HYB Master Shop"
                className="h-8 w-auto"
              />
              <span className="font-semibold text-stone-900 dark:text-white">
                HYB Shop
              </span>
            </div>
          )}

          {/* Mobile: Close button, Desktop: Collapse button */}
          <button
            onClick={() => {
              if (isMobileOrTablet) {
                setMobileSidebarOpen(false);
              } else {
                setSidebarCollapsed(!sidebarCollapsed);
              }
            }}
            className="p-1.5 rounded-md hover:bg-stone-100 dark:hover:bg-gray-700
                       active:scale-95 transition-all touch-target"
            aria-label={isMobileOrTablet ? t('sidebar.closeMenu') : t('sidebar.toggleSidebar')}
          >
            {isMobileOrTablet ? (
              <X className="w-5 h-5 text-stone-600 dark:text-gray-400" />
            ) : (
              <Menu className="w-5 h-5 text-stone-600 dark:text-gray-400" />
            )}
          </button>
        </div>

        {sidebarCollapsed && !isMobileOrTablet && (
          <div className="flex items-center justify-center py-4">
            <img src={logoUrl} alt="HYB Master Shop" className="h-8 w-auto" />
          </div>
        )}

        {/* Navigation */}
        <nav className="mt-4 flex-1 overflow-y-auto px-2">
          {visibleSidebarItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = currentPath === item.path;
            const showBadge = item.id === 'chat' && unreadCount > 0;

            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`
                  w-full flex items-center px-3 py-2.5 text-left rounded-lg mb-1
                  transition-all duration-200 relative touch-target
                  ${isActive
                    ? 'bg-gradient-to-r from-yellow-50 to-orange-50 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 shadow-sm border border-yellow-200'
                    : 'text-stone-700 dark:text-gray-300 hover:bg-stone-100 dark:hover:bg-gray-700'
                  }
                `}
              >
                <IconComponent className="w-5 h-5 flex-shrink-0" />
                {(!sidebarCollapsed || isMobileOrTablet) && (
                  <>
                    <span className="ml-3 text-sm font-medium">{t(item.labelKey)}</span>
                    {showBadge && (
                      <span className="ml-auto bg-red-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </>
                )}
                {sidebarCollapsed && !isMobileOrTablet && showBadge && (
                  <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-semibold w-4 h-4 rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div
          className="mt-auto w-full p-4 border-t border-stone-200 dark:border-gray-700"
          data-testid="sidebar-footer"
        >
          <div className="flex items-center justify-between">
            {(!sidebarCollapsed || isMobileOrTablet) && (
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-semibold">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-sm text-stone-700 dark:text-gray-300 font-medium truncate">
                  {user?.username}
                </span>
              </div>
            )}

            <div className="flex items-center space-x-1">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-md hover:bg-stone-100 dark:hover:bg-gray-700
                           active:scale-95 transition-all touch-target"
                title={t('sidebar.toggleDarkMode')}
                aria-label={t('sidebar.toggleDarkMode')}
              >
                {theme === 'dark' ? (
                  <Sun className="w-4 h-4 text-stone-600 dark:text-gray-400" />
                ) : (
                  <Moon className="w-4 h-4 text-stone-600 dark:text-gray-400" />
                )}
              </button>

              <button
                onClick={handleLogout}
                className="p-2 rounded-md hover:bg-stone-100 dark:hover:bg-gray-700
                           active:scale-95 transition-all touch-target"
                title={t('sidebar.signOut')}
                aria-label={t('sidebar.signOut')}
              >
                <LogOut className="w-4 h-4 text-stone-600 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-stone-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6 lg:py-4">
            {/* Mobile: Hamburger + Title */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {/* Hamburger Menu Button - Mobile/Tablet only */}
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-gray-700
                           active:scale-95 transition-all touch-target"
                aria-label={t('sidebar.openMenu')}
              >
                <Menu className="w-5 h-5 text-stone-700 dark:text-gray-300" />
              </button>

              {/* Page Title */}
              <div className="min-w-0 flex-1">
                <h1 className="text-lg lg:text-xl font-semibold text-stone-900 dark:text-white truncate">
                  {t(visibleSidebarItems.find(item => item.path === currentPath)?.labelKey || 'dashboard.title')}
                </h1>
                <p className="text-xs lg:text-sm text-stone-600 dark:text-gray-400 hide-mobile">
                  {t('dashboard.management')}
                </p>
              </div>
            </div>

            {/* Clock - Desktop only, compact on tablet */}
            <div className="flex items-center space-x-4">
              <div
                data-testid="dashboard-clock"
                className="text-xs lg:text-sm text-stone-600 dark:text-gray-400 font-mono"
              >
                {/* Mobile: Show only time, Desktop: Show full datetime */}
                <span className="lg:hidden">
                  {currentTime.toLocaleTimeString('th-TH', {
                    timeZone: 'Asia/Bangkok',
                    hour12: false,
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className="hidden lg:inline">
                  {currentTime.toLocaleString('th-TH', {
                    timeZone: 'Asia/Bangkok',
                    hour12: false,
                  })}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Maintenance Mode Warning Banner */}
        {isMaintenanceMode && (
          <div className="bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2 flex items-center justify-center gap-2">
            <AlertTriangle className="w-4 h-4 text-white flex-shrink-0" />
            <span className="text-white text-sm font-medium">
              {t('dashboard.maintenanceWarning')}
            </span>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-stone-50 dark:bg-gray-900">
          <div className="p-4 lg:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
